import React, { Dispatch, MouseEventHandler } from 'react';
import cn from 'classnames';
import styles from './TimelineNoteHeader.module.scss';
import NoteModel, { NoteReminder, NoteType } from '@models/Note';
import Avatar from '@components/Avatar';
import { ReminderBadgeIcon } from '@components/Icons/Reminder';
import Occasion from '@support/Occasion';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import { AddNoteIcon, PlusIcon, XIcon } from '@components/Icons';
import { TimelineItemUser } from '@models/Timeline';
import NoteIconAndColorPicker, { NoteIcon } from '@components/Note/IconAndColorPicker';
import NoteLabelEditor from '@components/Note/LabelEditor';
import { InteractionAccesory } from '@components/ContactCard';
import DateTimePicker from '@components/DateTimePicker';
import {
  AddNoteToReminderTooltip,
  DeleteReminderTooltip,
  EditReminderTooltip,
} from '@components/Tooltip/ReminderTooltip';

export const AddNoteBadge = ({ onClick }: { onClick: MouseEventHandler }) => (
  <div className={styles.addNoteIcon} onClick={onClick}>
    <PlusIcon width="8" height="8" strokeWidth="3" color="textSecondary" />
  </div>
);

export const NoteBadge = ({
  reminder,
  readOnly,
  noteType,
  updateNoteType,
  className,
  setSelectingIconAndColor,
  editable = true,
  addingReminder,
}: {
  reminder?: NoteReminder;
  readOnly?: boolean;
  noteType?: NoteType;
  updateNoteType?: (noteType: NoteType) => void;
  user: Partial<TimelineItemUser>;
  className?: string;
  setSelectingIconAndColor?: Dispatch<boolean>;
  editable?: boolean;
  addingReminder?: boolean;
}) => {
  if (reminder || addingReminder) {
    return <ReminderBadgeIcon date={reminder?.date} className={cn(styles.badge, className)} />;
  }

  if (readOnly) {
    return <InteractionAccesory interaction="contacts" />;
  }

  if (!editable) {
    return (
      <NoteIcon
        selectedColor={noteType?.color}
        selectedIconType={noteType?.icon}
        editable={false}
        className={className}
      />
    );
  }

  return (
    <NoteIconAndColorPicker
      setSelectingIconAndColor={setSelectingIconAndColor}
      noteType={noteType || { label: null, icon: null, color: null }}
      updateNoteType={updateNoteType}
    />
  );
};

export function ReminderDateDisplay({ date }: { date: Occasion }) {
  const dateDisplayFormat = date.isThisYear ? 'MMM dd' : 'MMM dd yyyy';

  return (
    <>
      {' '}
      <span className={cn(styles.reminderDate, !date.isPast && styles.upcoming)}>
        {date.format(dateDisplayFormat)}, {date.format('h:mm aa')}
      </span>
    </>
  );
}

function ReminderEditor({
  note,
  showEditNoteBtn,
  setEditingReminder,
  updateReminder,
  removeReminder,
  selectNote,
}: {
  note: NoteModel;
  showEditNoteBtn?: boolean;
  setEditingReminder?: Dispatch<boolean>;
  updateReminder: (date: Occasion) => void;
  removeReminder: () => void;
  selectNote: MouseEventHandler;
}) {
  const date = note.reminder?.date;
  const dateDisplayFormat = date.isThisYear ? 'MMM dd' : 'MMM dd yyyy';

  return (
    <>
      <small>&bull;</small>
      <div className={cn(styles.reminderEditor, date.isPast && styles.reminderInPast)}>
        <DateTimePicker
          dateTime={note.reminder?.date}
          setDateTime={updateReminder}
          trigger={
            <EditReminderTooltip>
              <div className={cn(styles.reminderDate, !date.isPast && styles.upcoming)}>
                {date.format(dateDisplayFormat)}, {date.format('h:mm aa')}
              </div>
            </EditReminderTooltip>
          }
          disabled={date.isPast}
          onOpen={() => setEditingReminder?.(true)}
          onClose={() => setTimeout(() => setEditingReminder?.(false), 150)}
        />
        <DeleteReminderTooltip deletesNote={showEditNoteBtn}>
          <button className={styles.reminderEditorBtn} onClick={removeReminder}>
            <XIcon width={11} height={11} />
          </button>
        </DeleteReminderTooltip>

        {showEditNoteBtn && (
          <AddNoteToReminderTooltip>
            <button className={styles.reminderEditorBtn} onClick={selectNote}>
              <AddNoteIcon
                width={14}
                height={14}
                style={{ transform: 'translate(-0.5px, -0.5px)' }}
              />
            </button>
          </AddNoteToReminderTooltip>
        )}
      </div>
    </>
  );
}

export const UserActionDescription = ({
  note,
  noteLabel,
  updateNoteLabel,
  user,
  creatingNewNote,
  noteSelected,
  setEditingReminder,
  updateReminder,
  removeReminder,
  isSettingNewReminder,
  showEditNoteBtn,
  selectNote,
}: {
  note: NoteModel;
  noteLabel: string | null;
  updateNoteLabel: (label: string) => void;
  user: Partial<TimelineItemUser>;
  creatingNewNote?: boolean;
  noteSelected?: boolean;
  setEditingReminder?: Dispatch<boolean>;
  updateReminder?: (date: Occasion) => void;
  removeReminder?: () => void;
  isSettingNewReminder?: boolean;
  showEditNoteBtn?: boolean;
  selectNote?: MouseEventHandler;
}) => {
  const { isMultiPlayer } = useWorkspaceContext();

  if (note?.isReadOnly) {
    return <div className={styles.author}>Note via {note.importSource}</div>;
  }

  let userAction = 'added a note';
  let reminderDate = null;
  let userNameDisplay = (
    <b className={styles.authorDisplay}>
      <div className={styles.avatarContainer}>
        <Avatar url={user.avatar_url} className={styles.avatar} />
      </div>
      {user.name}&nbsp;
    </b>
  );

  if (!isMultiPlayer || user.is_self) {
    userNameDisplay = <>You&nbsp;</>;
  }

  if (creatingNewNote) {
    userAction = 'Adding a note…';
    userNameDisplay = null;
  }

  if (note?.reminder) {
    userAction = 'set a reminder';
    reminderDate = (
      <>
        &nbsp;
        <ReminderEditor
          note={note}
          showEditNoteBtn={showEditNoteBtn}
          setEditingReminder={setEditingReminder}
          updateReminder={updateReminder}
          removeReminder={removeReminder}
          selectNote={selectNote}
        />
      </>
    );
  }

  if ((creatingNewNote && note?.reminder) || (creatingNewNote && isSettingNewReminder)) {
    userAction = 'Setting a reminder';
  }

  return (
    <div className={styles.author}>
      {note && (
        <>
          <NoteLabelEditor
            key={String(creatingNewNote)}
            label={noteLabel}
            updateLabel={updateNoteLabel}
            hidden={!noteLabel && !noteSelected}
            appendBullet
            className={styles.noteLabelEditor}
          />
          <span className={styles.actionDescription}>
            {userNameDisplay}
            {userAction}
          </span>
          {reminderDate}
        </>
      )}
    </div>
  );
};
